import React, { useEffect, useMemo, useRef, useState } from 'react';
import AccountNav from '../../components/AccountNav/AccountNav';
import AccountNavMobile from '../../components/AccountNavMobile/AccountNavMobile';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import './MarketPlace.scss';
import MarketHero from '../../components/MarketHero/MarketHero';
import MarketCard from '../../components/MarketCard/MarketCard';
import MarketFooter from '../../components/MarketFooter/MarketFooter';
import MarketSlider from '../../components/MarketSlider/MarketSlider';
import { fetchInitialOfferingListAction, fetchMoreInitialSliderAction } from '../../store/reducer/initialOfferingSlice';
import { fetchSecondaryOfferingListAction, fetchMoreSecondarySliderAction } from '../../store/reducer/secondaryOfferingSlice';
import useGetUserProfile from '../../Hooks/useGetUserProfile';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';

const MarketPlace = () => {
  const shouldLog = useRef(true);
  const initialMarketpropertyList = useSelector((state) => state.initialOffering.sliderData);
  const loadingInitial = useSelector((state) => state.initialOffering.loading);
  const secondaryMarketPropertyList = useSelector((state) => state.secondaryOffering.sliderData);
  const loadingSecondary = useSelector((state) => state.secondaryOffering.loading);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isEntityRestricted } = useSecondaryMarketEligibility();
  // Secondary Market is only shown to signed-in users.
  const isLoggedIn = !!localStorage.getItem('access_token');
  const canViewSecondary = isLoggedIn && !isEntityRestricted;

  const [initialPage, setInitialPage] = useState(1);
  const [secondaryPage, setSecondaryPage] = useState(1);
  const [initialLoadingMore, setInitialLoadingMore] = useState(false);
  const [secondaryLoadingMore, setSecondaryLoadingMore] = useState(false);

  const initialHasMore =
    (initialMarketpropertyList?.data?.properties?.length ?? 0) < (initialMarketpropertyList?.data?.count ?? 0);
  const secondaryHasMore =
    (secondaryMarketPropertyList?.data?.properties?.length ?? 0) < (secondaryMarketPropertyList?.data?.count ?? 0);

  const featchInitialOfferingData = async () => {
    const onFetchFail = () => {
      if (location?.state?.prevPath) {
        navigate(location.state.prevPath);
      }
      navigate(`/`);
    };
    await dispatch(fetchInitialOfferingListAction({ page: 1, perPage: 9,  sortType: 'Newest', onFail: onFetchFail }));
  };

 const featchSecondaryOfferingData = async () => {
  // Skip for signed-out users and Entity accounts — they're not supported on the Secondary Marketplace (RDS-921).
  if (canViewSecondary) {
    await dispatch(fetchSecondaryOfferingListAction({ page: 1, perPage: 9 }));
  }
};

  const handleLoadMoreInitial = () => {
    if (initialLoadingMore || !initialHasMore) return;
    const nextPage = initialPage + 1;
    setInitialPage(nextPage);
    setInitialLoadingMore(true);
    dispatch(fetchMoreInitialSliderAction({ page: nextPage, perPage: 9, sortType: 'Newest' }));
  };

  const handleLoadMoreSecondary = () => {
    if (!canViewSecondary) return;
    if (secondaryLoadingMore || !secondaryHasMore) return;
    const nextPage = secondaryPage + 1;
    setSecondaryPage(nextPage);
    setSecondaryLoadingMore(true);
    dispatch(fetchMoreSecondarySliderAction({ page: nextPage, perPage: 9 }));
  };

  // Reset loadingMore once new properties arrive in Redux (fetch resolved)
  useEffect(() => {
    setInitialLoadingMore(false);
  }, [initialMarketpropertyList?.data?.properties?.length]);

  useEffect(() => {
    setSecondaryLoadingMore(false);
  }, [secondaryMarketPropertyList?.data?.properties?.length]);

  // Read `isSecondaryPaused` from the secondary offering API response.
  // If the API returns `data.isSecondaryPaused: true`, the secondary slider will be paused.
  const isSecondaryPaused = useMemo(() => {
    return secondaryMarketPropertyList?.data?.isSecondaryMarketPaused === true;
  }, [secondaryMarketPropertyList?.data?.isSecondaryMarketPaused]);

  useGetUserProfile();
  useEffect(() => {
    if (shouldLog.current) {
      shouldLog.current = false;
      featchInitialOfferingData();
      featchSecondaryOfferingData();
    }
  }, []);
  return (
    <div className='marketplace'>
      <div className="sticky-nav">
        <AccountNav />
      </div>
      <div className="sticky-nav">
        <AccountNavMobile />
      </div>
      <MarketHero />
      <MarketCard />
      {/* === Check the notification implementation here === */}
      {/* <Button onClick={() => toastNotify('newAgreement', <ListingAgreementNotification onBtnClick={() => null} />)}>Click me</Button>
      <ListingAgreement /> */}
      {(loadingInitial || initialMarketpropertyList?.data?.properties?.length > 0) && (
        <MarketSlider
          market='Initial Offerings'
          marketLink='/marketplace/initial/'
          initialOfferingData={initialMarketpropertyList}
          initialLoading={loadingInitial}
          isSecondary={false}
          onLoadMore={handleLoadMoreInitial}
          hasMore={initialHasMore}
          loadingMore={initialLoadingMore}
        />
      )}
      {canViewSecondary && (
        (loadingSecondary || secondaryMarketPropertyList?.data?.properties?.length > 0) && (
          <MarketSlider
            market={'Realbricks Market'}
            marketLink='/marketplace/secondary/'
            secondaryMarketData={secondaryMarketPropertyList}
            secondaryLoading={loadingSecondary}
            isSecondary={true}
            isSecondaryMarketPaused={isSecondaryPaused}
            onLoadMore={handleLoadMoreSecondary}
            hasMore={secondaryHasMore}
            loadingMore={secondaryLoadingMore}
          />
        )
      )}
      {/* Temporary comment as secondary market not lunched yet */}
      {/* <ResourcesCard /> */}
      <MarketFooter />
    </div>
  );
};

export default MarketPlace;
